import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { COLORS } from '../styles/theme';

interface NavItem {
  path: string;
  label: string;
  icon: string;
}

const NAV_ITEMS: NavItem[] = [
  { path: '/', label: 'Dashboard', icon: 'grid' },
  { path: '/properties', label: 'Properties', icon: 'square' },
  { path: '/camps', label: 'Base Camps', icon: 'home' },
  { path: '/camp-sites', label: 'Camp Sites', icon: 'tent' },
  { path: '/equipment', label: 'Equipment', icon: 'tool' },
  { path: '/trails', label: 'Trails', icon: 'trending-up' },
  { path: '/pois', label: 'Points of Interest', icon: 'map-pin' },
  { path: '/areas', label: 'Areas', icon: 'layers' },
  { path: '/map', label: 'Map', icon: 'map' },
  { path: '/groups', label: 'Groups', icon: 'message-square' },
  { path: '/events', label: 'Events', icon: 'star' },
  { path: '/packs', label: 'Packs', icon: 'users' },
  { path: '/users', label: 'Users', icon: 'user' },
  { path: '/almanac', label: 'Almanac', icon: 'calendar' },
  { path: '/approval-requests', label: 'Approval Requests', icon: 'check-circle' },
  { path: '/export', label: 'Export', icon: 'download' },
  { path: '/audit', label: 'Audit Logs', icon: 'list' },
];

// Simple SVG icons for the sidebar
function SidebarIcon({ name }: { name: string }) {
  const iconPaths: Record<string, string> = {
    grid: 'M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z',
    home: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1',
    tool: 'M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z',
    users: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 7a4 4 0 100 8 4 4 0 000-8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75',
    user: 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 7a4 4 0 100 8 4 4 0 000-8z',
    calendar: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
    map: 'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7',
    download: 'M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3',
    list: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
    'message-square': 'M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z',
    star: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z',
    'check-circle': 'M22 11.08V12a10 10 0 11-5.93-9.14M22 4L12 14.01l-3-3',
    tent: 'M3 21h18L12 3 3 21zm9-18l4.5 9H7.5L12 3z',
    'map-pin': 'M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z',
    'log-out': 'M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9',
  };

  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={iconPaths[name] || iconPaths.grid} />
    </svg>
  );
}

export function Sidebar() {
  const { signOut, user } = useAuth();

  return (
    <aside style={styles.sidebar}>
      {/* Logo / Title */}
      <div style={styles.header}>
        <div style={styles.logoText}>GL</div>
        <div style={styles.headerText}>
          <div style={styles.title}>Grimsthorpe</div>
          <div style={styles.subtitle}>Admin</div>
        </div>
      </div>

      {/* Navigation */}
      <nav style={styles.nav}>
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/'}
            style={({ isActive }) => ({
              ...styles.navLink,
              ...(isActive ? styles.navLinkActive : {}),
            })}
          >
            <SidebarIcon name={item.icon} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div style={styles.footer}>
        <div style={styles.userEmail}>{user?.email}</div>
        <button
          onClick={() => signOut()}
          style={styles.signOutBtn}
        >
          <SidebarIcon name="log-out" />
          <span>Sign Out</span>
        </button>
      </div>
    </aside>
  );
}

const styles: Record<string, React.CSSProperties> = {
  sidebar: {
    width: 240,
    height: '100vh',
    backgroundColor: COLORS.backgroundSidebar,
    borderRight: `1px solid ${COLORS.borderDefault}`,
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    left: 0,
    top: 0,
    zIndex: 100,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '20px 16px',
    borderBottom: `1px solid ${COLORS.borderDefault}`,
  },
  logoText: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: COLORS.primaryGreen,
    color: COLORS.textGold,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
    fontSize: 14,
    letterSpacing: 1,
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
    color: COLORS.textPrimary,
    lineHeight: '1.2',
  },
  subtitle: {
    fontSize: 11,
    color: COLORS.brass,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: 1,
  },
  nav: {
    flex: 1,
    padding: '12px 8px',
    overflowY: 'auto' as const,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  navLink: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '9px 12px',
    borderRadius: 6,
    color: COLORS.textMuted,
    fontSize: 13,
    fontWeight: 500,
    textDecoration: 'none',
    transition: 'all 0.15s ease',
  },
  navLinkActive: {
    backgroundColor: COLORS.primaryGreen,
    color: COLORS.brassLight,
    fontWeight: 600,
  },
  footer: {
    padding: '12px 16px',
    borderTop: `1px solid ${COLORS.borderDefault}`,
  },
  userEmail: {
    fontSize: 11,
    color: COLORS.textMuted,
    marginBottom: 8,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  signOutBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: 'none',
    border: 'none',
    color: COLORS.textMuted,
    fontSize: 13,
    cursor: 'pointer',
    padding: '6px 0',
    width: '100%',
  },
};
